import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Check,
  Link2,
  Loader2,
  MessageCircle,
  ShieldOff,
  Swords,
  UserCheck,
  UserMinus,
  UserPlus,
  UserX,
  X,
} from 'lucide-react';

import { UserAvatar } from '@/components/social/UserAvatar';
import { PresenceBadge } from '@/components/social/PresenceBadge';
import { GuardaLive } from '@/components/social/GuardaLive';
import { ChallengeOptionsSheet } from '@/components/social/ChallengeOptionsSheet';
import { SfidaSheetDaCatalogo } from '@/components/social/SfidaSheet';
import { Loader } from '@/components/common/Loader';
import { useHaptics } from '@/hooks/useHaptics';
import { useSfidaDiPiattaforma } from '@/hooks/useSfidaDiPiattaforma';
import {
  useAcceptFriendshipMutation,
  useBlockUserMutation,
  useFollowUserMutation,
  useGetUserPresenceQuery,
  useGetUserProfileQuery,
  useRemoveFriendshipMutation,
  useRequestFriendshipMutation,
  useUnblockUserMutation,
  useUnfollowUserMutation,
} from '@/services/socialApi';
import {
  useCreateChallengeMutation,
  useGetMyLinkQuery,
} from '@/services/qduelsApi';
import { classifyQduelsError, isNotLinkedError } from '@/services/qduelsErrors';
import type { PublicProfileDTO, PresenceStatus } from '@/types/social';
import type { DuelFormat, DuelGameMode } from '@/types/qduels';
import { cn } from '@/lib/cn';
import { env } from '@/env';
import { getMyLogtoSub } from '@/lib/logto';
import { percorsoConversazione, rifiutoDm } from '@/lib/qsocials/dm';
import { liveMatchDaMostrare } from '@/lib/qsocials/liveMatch';
import { useOpenDmConversationMutation } from '@/services/dmApi';

export function UserProfile() {
  const { t } = useTranslation('general');
  const { id } = useParams<{ id: string }>();

  /**
   * `/profile/<il MIO id>` e' il MIO profilo, non quello di un altro (ADR-0196 §3, backlog dichiarato dal 04/10): un collegamento
   * diretto, un tocco sul mio nome in una lista, in classifica o in chat arrivano qui con il mio sub. Aprirlo come «un altro
   * giocatore» mostrava la voce sbagliata della barra (`/profile/*` accende «Amici») e un profilo senza lo zaino. La cura e' il
   * rimando a `/profile`, non una regola della barra. `replace`: l'indietro non deve tornare allo specchio.
   *
   * L'id della rotta e' il sub di Logto (= `userId` di QSocials, `types/social.ts`: lo passano `FriendListItem`, le conversazioni
   * DM, la ricerca amici e lo storico di QGammon, letti nei chiamanti di `/profile/${…}`), e il mio lo dice `getMyLogtoSub()` — la
   * stessa fonte di leaderboard, DM e tempo reale. NON `useQlashUser().id`: quello e' l'id di Laravel o di Limulo, un altro spazio. Se il mio sub non si sa (nessun token:
   * `null`) NON si rimanda: «non lo so» non e' «sono io», e la schermata fa come prima. ⚠️ Il token si legge una volta per render
   * da localStorage, non e' reattivo: ma la rotta si monta solo da autenticati, quando il token c'e' gia'.
   */
  const mio = getMyLogtoSub();
  const eIlMio = !!id && mio !== null && id === mio;

  const { data, isLoading, isError, error } = useGetUserProfileQuery(id ?? '', {
    // Il mio profilo non si chiede come quello di un altro: si rimanda prima che parta una richiesta.
    skip: !id || eIlMio,
  });

  if (eIlMio) return <Navigate to="/profile" replace />;

  if (!id || (isError && isNotFound(error))) {
    return (
      <Centered>
        <UserX className="h-10 w-10 text-fg-muted" aria-hidden />
        <p className="text-sm text-fg-muted">
          {t('social.profile_not_found', { defaultValue: 'User not found.' })}
        </p>
      </Centered>
    );
  }

  if (isLoading || (!data && !isError)) {
    return (
      <Centered>
        <Loader />
      </Centered>
    );
  }

  if (isError || !data) {
    return (
      <Centered>
        <p className="text-sm text-danger">
          {t('social.profile_load_error', {
            defaultValue: "Couldn't load this profile.",
          })}
        </p>
      </Centered>
    );
  }

  return <ProfileView profile={data} />;
}

function ProfileView({ profile }: { profile: PublicProfileDTO }) {
  const { t } = useTranslation('general');
  const userId = profile.userId;
  const name =
    profile.displayName?.trim() ||
    t('social.unknown_user', { defaultValue: 'QLASH player' });
  const presence = profile.presence;
  // `GET /social/users/:id` porta la presenza SENZA `liveMatch` (il contratto r38 lo mette su
  // `GET /social/presence/:userId`): per un amico (`presence` non nulla) si legge quella, la stessa cache delle righe
  // di Amici. Per me stesso no: «sta giocando» e' per gli amici. Campo assente = niente (`liveMatchDaMostrare`).
  const presenzaLive = useGetUserPresenceQuery(userId, { skip: profile.isSelf || !presence });
  const live = liveMatchDaMostrare(
    presenzaLive.data?.presence.liveMatch,
    presenzaLive.data?.presence.status,
  );

  return (
    <div className="flex flex-col items-center gap-5 px-4 pb-8 pt-6">
      {/* Identity */}
      <div className="flex flex-col items-center gap-2">
        <div className="relative">
          <UserAvatar
            userId={userId}
            label={name}
            avatarUrl={profile.avatarUrl}
            size="lg"
            className="h-20 w-20 text-2xl"
          />
          {presence && (
            <span className="absolute bottom-0.5 right-0.5 rounded-full bg-bg p-0.5">
              <PresenceBadge status={presence.status} />
            </span>
          )}
        </div>
        <h1 className="max-w-[16rem] truncate text-center text-xl font-bold text-fg">
          {name}
        </h1>
        {live && (
          <>
            <p className="text-xs font-medium text-brand" data-testid="sta-giocando">
              {t('social.row_playing', { defaultValue: 'Playing now' })}
            </p>
            <GuardaLive live={live} />
          </>
        )}
        {!live && presence && presence.status !== 'offline' && (
          <p className="text-xs font-medium text-brand">
            {presenceLabel(presence.status, t)}
          </p>
        )}
      </div>

      {/* Counts */}
      <div className="flex w-full max-w-xs items-center justify-around rounded-2xl bg-bg-secondary py-3">
        <Stat
          value={profile.counts.friends}
          label={t('social.profile_friends_label', { defaultValue: 'Friends' })}
        />
        <Divider />
        <Stat
          value={profile.counts.followers}
          label={t('social.profile_followers_label', {
            defaultValue: 'Followers',
          })}
        />
        <Divider />
        <Stat
          value={profile.counts.following}
          label={t('social.profile_following_label', {
            defaultValue: 'Following',
          })}
        />
      </div>

      {profile.relationship.mutualFriends > 0 && (
        <p className="-mt-2 text-xs text-fg-muted">
          {t('social.profile_mutual', {
            count: profile.relationship.mutualFriends,
            defaultValue: '{{count}} mutual friends',
          })}
        </p>
      )}

      {/* Actions */}
      <div className="w-full max-w-xs">
        {profile.isSelf ? (
          <p className="text-center text-sm text-fg-muted">
            {t('social.profile_you', { defaultValue: 'This is you.' })}
          </p>
        ) : (
          <RelationshipActions profile={profile} name={name} />
        )}
      </div>
    </div>
  );
}

function RelationshipActions({
  profile,
  name,
}: {
  profile: PublicProfileDTO;
  name: string;
}) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const userId = profile.userId;
  const { friendStatus, iFollow } = profile.relationship;

  const [requestFriendship, req] = useRequestFriendshipMutation();
  const [acceptFriendship, acc] = useAcceptFriendshipMutation();
  const [removeFriendship, rem] = useRemoveFriendshipMutation();
  const [blockUser, blk] = useBlockUserMutation();
  const [unblockUser, unblk] = useUnblockUserMutation();
  const [followUser, fol] = useFollowUserMutation();
  const [unfollowUser, unfol] = useUnfollowUserMutation();

  const friendBusy =
    req.isLoading || acc.isLoading || rem.isLoading || blk.isLoading || unblk.isLoading;
  const followBusy = fol.isLoading || unfol.isLoading;

  const act =
    (fn: () => void, impact: Parameters<typeof haptics.impact>[0] = 'light') =>
    () => {
      if (friendBusy) return;
      haptics.impact(impact);
      fn();
    };

  const onFollowToggle = () => {
    if (followBusy) return;
    haptics.impact('light');
    if (iFollow) unfollowUser({ followedUserId: userId });
    else followUser({ followedUserId: userId });
  };

  // they blocked me → no actions, just a muted state.
  if (friendStatus === 'blocked_by_them') {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl bg-bg-secondary py-3 text-sm text-fg-muted">
        <ShieldOff className="h-4 w-4" aria-hidden />
        {t('social.profile_blocked_you', {
          defaultValue: 'This user has blocked you.',
        })}
      </div>
    );
  }

  // I blocked them → the only action is to unblock.
  if (friendStatus === 'blocked_by_me') {
    return (
      <ActionButton
        variant="secondary"
        busy={friendBusy}
        onClick={act(() => unblockUser({ friendUserId: userId }), 'medium')}
        icon={<UserCheck className="h-4 w-4" />}
        label={t('social.profile_unblock', { defaultValue: 'Unblock' })}
      />
    );
  }

  const followToggle = (
    <ActionButton
      variant="secondary"
      busy={followBusy}
      onClick={onFollowToggle}
      icon={
        iFollow ? <UserCheck className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />
      }
      label={
        iFollow
          ? t('social.following', { defaultValue: 'Following' })
          : t('social.follow', { defaultValue: 'Follow' })
      }
    />
  );

  return (
    <div className="flex flex-col gap-2">
      {friendStatus === 'none' && (
        <ActionButton
          variant="primary"
          busy={friendBusy}
          onClick={act(() => requestFriendship({ friendUserId: userId }))}
          icon={<UserPlus className="h-4 w-4" />}
          label={t('social.add_friend', { defaultValue: 'Add friend' })}
        />
      )}

      {friendStatus === 'pending_outgoing' && (
        <ActionButton
          variant="secondary"
          busy={friendBusy}
          onClick={act(() => removeFriendship({ friendUserId: userId }))}
          icon={<X className="h-4 w-4" />}
          label={t('social.request_sent', { defaultValue: 'Request sent' })}
        />
      )}

      {friendStatus === 'pending_incoming' && (
        <div className="flex gap-2">
          <ActionButton
            className="flex-1"
            variant="primary"
            busy={friendBusy}
            onClick={act(() => acceptFriendship({ friendUserId: userId }), 'medium')}
            icon={<Check className="h-4 w-4" />}
            label={t('social.accept', { defaultValue: 'Accept' })}
          />
          <ActionButton
            className="flex-1"
            variant="secondary"
            busy={friendBusy}
            onClick={act(() => removeFriendship({ friendUserId: userId }), 'medium')}
            icon={<X className="h-4 w-4" />}
            label={t('social.decline', { defaultValue: 'Decline' })}
          />
        </div>
      )}

      {friendStatus === 'accepted' && (
        <>
          <ChallengeAction opponentId={userId} opponentName={name} />
          {env.featureDmEnabled && profile.relationship.canMessage ? <ScriviAction friendUserId={userId} /> : null}
          <ActionButton
            variant="secondary"
            busy={friendBusy}
            onClick={act(() => removeFriendship({ friendUserId: userId }), 'medium')}
            icon={<UserMinus className="h-4 w-4" />}
            label={t('social.profile_friends_badge', { defaultValue: 'Friends' })}
          />
        </>
      )}

      {followToggle}

      <button
        type="button"
        disabled={friendBusy}
        onClick={act(() => blockUser({ friendUserId: userId }), 'heavy')}
        className="mt-1 self-center text-xs text-fg-muted underline transition-opacity active:opacity-70 disabled:opacity-50"
      >
        {t('social.profile_block', { defaultValue: 'Block user' })}
      </button>
    </div>
  );
}

/**
 * «Scrivi» — apre (o crea: la rotta e' idempotente) la conversazione DM con un amico e ci porta dentro.
 * Solo con `canMessage` (amicizia accettata) e con i DM accesi nella build. Il 403 si dice a parole (D2).
 */
function ScriviAction({ friendUserId }: { friendUserId: string }) {
  const { t } = useTranslation('general');
  const navigate = useNavigate();
  const haptics = useHaptics();
  const [apri, { isLoading }] = useOpenDmConversationMutation();
  const [errore, setErrore] = useState<string | null>(null);
  const scrivi = async () => {
    haptics.impact('light');
    setErrore(null);
    try {
      const c = await apri(friendUserId).unwrap();
      navigate(percorsoConversazione(c.id));
    } catch (e) {
      setErrore(rifiutoDm(e) === 'chiusa' ? t('social.dm.chiusaTesto') : t('social.dm.apriErrore'));
    }
  };
  return (
    <>
      <ActionButton
        variant="secondary"
        busy={isLoading}
        onClick={() => void scrivi()}
        icon={<MessageCircle className="h-4 w-4" />}
        label={t('social.dm.scrivi')}
      />
      {errore ? (
        <p role="alert" className="text-center text-xs text-danger">
          {errore}
        </p>
      ) : null}
    </>
  );
}

/**
 * "Sfida" (1v1 duel) action for an accepted friend. Mirrors the challenge flow
 * in <FriendListItem>: probe the caller's Clash Royale link up front (404 = not
 * linked → route to /duels/link), otherwise open the options sheet.
 * NOTE: duplicates FriendListItem's challenge logic — extract a shared
 * useDuelChallenge hook when this is next touched.
 */
function ChallengeAction({
  opponentId,
  opponentName,
}: {
  opponentId: string;
  opponentName: string;
}) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const navigate = useNavigate();

  // I-207: come in <FriendListItem>, dietro `useSfidaDiPiattaforma` il bottone apre la sfida di
  // piattaforma e la sonda del collegamento Clash Royale non serve.
  const sfidaPiattaforma = useSfidaDiPiattaforma();
  const [sfidaAperta, setSfidaAperta] = useState(false);
  const myLinkQuery = useGetMyLinkQuery(undefined, { skip: sfidaPiattaforma });
  const challengerNotLinked = !sfidaPiattaforma && isNotLinkedError(myLinkQuery.error);

  const [createChallenge, creating] = useCreateChallengeMutation();
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const onTap = () => {
    if (creating.isLoading || sent) return;
    if (sfidaPiattaforma) {
      haptics.impact('light');
      setSfidaAperta(true);
      return;
    }
    if (challengerNotLinked) {
      haptics.impact('light');
      navigate('/duels/link');
      return;
    }
    haptics.impact('light');
    setMessage(null);
    setSheetOpen(true);
  };

  const submit = async ({
    format,
    gameMode,
  }: {
    format: DuelFormat;
    gameMode: DuelGameMode;
  }) => {
    if (creating.isLoading || sent) return;
    haptics.impact('medium');
    try {
      await createChallenge({
        defenderLogtoId: opponentId,
        format,
        gameMode,
        createdVia: 'telegram',
      }).unwrap();
      haptics.notification('success');
      setSent(true);
      setSheetOpen(false);
      setMessage(t('social.challenge_sent', { defaultValue: 'Challenge sent!' }));
    } catch (err) {
      haptics.notification('error');
      setSheetOpen(false);
      const kind = classifyQduelsError(err);
      if (kind === 'challenger_not_linked') void myLinkQuery.refetch();
      setMessage(
        kind === 'defender_not_linked'
          ? t('social.challenge_defender_not_linked', {
              defaultValue:
                "This friend hasn't linked their Clash Royale account yet.",
            })
          : kind === 'challenger_not_linked'
            ? t('social.challenge_challenger_not_linked', {
                defaultValue:
                  'Link your Clash Royale account first to send challenges.',
              })
            : kind === 'cannot_challenge_self'
              ? t('social.challenge_cannot_self', {
                  defaultValue: "You can't challenge yourself.",
                })
              : t('social.challenge_error', {
                  defaultValue: 'Could not send the challenge. Try again.',
                }),
      );
    }
  };

  return (
    <>
      <ActionButton
        variant="primary"
        busy={creating.isLoading}
        disabled={sent}
        onClick={onTap}
        icon={
          sent ? (
            <Check className="h-4 w-4" />
          ) : challengerNotLinked ? (
            <Link2 className="h-4 w-4" />
          ) : (
            <Swords className="h-4 w-4" />
          )
        }
        label={
          sent
            ? t('social.challenge_sent_short', { defaultValue: 'Sent' })
            : t('social.challenge', { defaultValue: 'Challenge' })
        }
      />
      {message && (
        <p
          className={cn('text-center text-[11px]', sent ? 'text-success' : 'text-danger')}
          role="status"
        >
          {message}
        </p>
      )}
      {sheetOpen && (
        <ChallengeOptionsSheet
          open
          opponentName={opponentName}
          busy={creating.isLoading}
          onSubmit={submit}
          onClose={() => setSheetOpen(false)}
        />
      )}
      {sfidaAperta && <SfidaSheetDaCatalogo avversario={opponentName} amicoId={opponentId} onClose={() => setSfidaAperta(false)} />}
    </>
  );
}

function ActionButton({
  variant,
  busy,
  disabled,
  onClick,
  icon,
  label,
  className,
}: {
  variant: 'primary' | 'secondary';
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      className={cn(
        'flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-opacity active:opacity-80 disabled:opacity-50',
        variant === 'primary'
          ? 'bg-brand text-brand-fg'
          : 'bg-bg-secondary text-fg',
        className,
      )}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {label}
    </button>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center px-2">
      <span className="text-lg font-bold text-fg">{value}</span>
      <span className="text-[11px] uppercase tracking-wide text-fg-muted">
        {label}
      </span>
    </div>
  );
}

function Divider() {
  return <span className="h-8 w-px bg-bg-tertiary" aria-hidden />;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-16">
      {children}
    </div>
  );
}

function presenceLabel(
  status: PresenceStatus,
  t: (k: string, o: { defaultValue: string }) => string,
): string {
  switch (status) {
    case 'in_tournament':
      return t('social.row_in_tournament', { defaultValue: 'In tournament' });
    case 'watching':
      return t('social.row_watching', { defaultValue: 'Watching live' });
    case 'idle':
      return t('social.row_idle', { defaultValue: 'Idle' });
    default:
      return t('social.row_online', { defaultValue: 'Online' });
  }
}

/** True when the getUserProfile query errored with a 404 (unknown sub). */
function isNotFound(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status?: unknown }).status === 404
  );
}
